import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, ArrowRight, Check } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import SparkLogo from '../components/SparkLogo';

/**
 * Reset password page — arrived here from the email link Supabase sends.
 * Supabase auto-detects the recovery token from the URL hash and opens a
 * recovery session that allows us to call `supabase.auth.updateUser({ password })`.
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [recoveryReady, setRecoveryReady] = useState(false);

  // Wait for Supabase to detect the recovery token in the URL hash
  useEffect(() => {
    const check = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) setRecoveryReady(true);
    };
    check();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setRecoveryReady(true);
      }
    });
    return () => sub?.subscription?.unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('A senha precisa ter pelo menos 6 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('As senhas não coincidem.');
      return;
    }
    setLoading(true);
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password });
      if (updErr) throw updErr;
      setDone(true);
      setTimeout(() => navigate('/auth?reset=ok', { replace: true }), 2000);
    } catch (err) {
      setError(err.message || 'Falha ao atualizar a senha. Solicite um novo link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white flex items-center justify-center px-5 py-12 relative">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(79,172,254,0.18), transparent 70%)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,167,38,0.18), transparent 70%)' }} />
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-md">
        <div className="flex justify-center mb-6"><SparkLogo height={72} /></div>
        <div className="glass-card rounded-3xl p-7 sm:p-8" data-testid="reset-password-card">
          {done ? (
            <div className="text-center py-4">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-green-500/20 text-green-400 flex items-center justify-center mb-4">
                <Check size={24} />
              </div>
              <h1 className="heading-display text-xl font-bold">Senha atualizada!</h1>
              <p className="text-xs text-slate-400 mt-2">Redirecionando para o login...</p>
            </div>
          ) : (
            <>
              <h1 className="heading-display text-2xl font-bold text-center">Defina sua nova senha</h1>
              <p className="text-xs text-slate-400 text-center mt-1.5">
                {recoveryReady ? 'Crie uma senha forte para sua conta GetSpark.' : 'Validando o link de recuperação...'}
              </p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-3">
                <div className="relative">
                  <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="password" required minLength={6}
                    placeholder="Nova senha (mín. 6 caracteres)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-input-password"
                    autoComplete="new-password"
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#4facfe]/50"
                  />
                </div>
                <div className="relative">
                  <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="password" required minLength={6}
                    placeholder="Confirme a nova senha"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-input-confirm"
                    autoComplete="new-password"
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#4facfe]/50"
                  />
                </div>
                {error && (
                  <div data-testid="reset-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</div>
                )}
                <button
                  type="submit"
                  disabled={loading || !recoveryReady}
                  data-testid="reset-submit"
                  className="w-full btn-primary rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? 'Salvando...' : <>Salvar nova senha <ArrowRight size={14} /></>}
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
